import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ClipboardEdit, Save, Trash2, RefreshCw, AlertTriangle, Filter } from 'lucide-react';
import { toast } from 'sonner';

import { apiFetch } from '../../services/api';
const API = process.env.REACT_APP_BACKEND_URL;
const toLocalDateTimeInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    // yyyy-MM-ddTHH:mm im lokalen Zeitraum
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

const localInputToIso = (val) => (val ? new Date(val).toISOString() : null);

const formatDuration = (mins) => {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${String(m).padStart(2, '0')}min`;
};

/**
 * Chef-Ansicht zur manuellen Korrektur von Zeit-Buchungen.
 * Nutzt neue Endpoints:
 *   GET    /api/pro/time/entries
 *   PATCH  /api/pro/time/entries/{id}
 *   DELETE /api/pro/time/entries/{id}
 */
const TimeCorrectionPanel = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(14);
  const [statusFilter, setStatusFilter] = useState('');
  const [editing, setEditing] = useState({}); // id -> patch
  const [saving, setSaving] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (statusFilter) params.set('status', statusFilter);
      const res = await apiFetch(`${API}/api/pro/time/entries?${params.toString()}`, { headers: {} });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler beim Laden');
      setEntries(data.entries || []);
    } catch (err) {
      toast.error(err.message || 'Konnte Einträge nicht laden');
    } finally {
      setLoading(false);
    }
  }, [days, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const setPatch = (id, key, value) => {
    setEditing((prev) => ({
      ...prev,
      [id]: { ...(prev[id] || {}), [key]: value } }));
  };

  const save = async (entry) => {
    const patch = editing[entry._id] || {};
    if (Object.keys(patch).length === 0) {
      toast.info('Keine Änderungen');
      return;
    }
    setSaving((s) => ({ ...s, [entry._id]: true }));
    try {
      const body = {
        started_at: patch.started_at ? localInputToIso(patch.started_at) : undefined,
        ended_at: patch.ended_at ? localInputToIso(patch.ended_at) : undefined,
        note: patch.note };
      const res = await apiFetch(`${API}/api/pro/time/entries/${entry._id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json'},
        body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler beim Speichern');
      toast.success('Eintrag korrigiert', { description: `Neue Dauer: ${formatDuration(data.duration_minutes)}` });
      setEditing((prev) => {
        const c = { ...prev }; delete c[entry._id]; return c;
      });
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving((s) => ({ ...s, [entry._id]: false }));
    }
  };

  const remove = async (entry) => {
    if (!window.confirm(`Eintrag von ${entry.employee_name} wirklich löschen?`)) return;
    try {
      const res = await apiFetch(`${API}/api/pro/time/entries/${entry._id}`, {
        method: 'DELETE', headers: {} });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.detail || 'Fehler');
      toast.success('Eintrag gelöscht');
      load();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-gray-950 to-black border-yellow-500/30" data-testid="time-correction-panel">
      <CardHeader>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <ClipboardEdit className="h-5 w-5 text-yellow-400" /> Zeit-Korrektur
            </CardTitle>
            <CardDescription className="text-gray-400">
              Manuelle Nachbearbeitung von Zeit-Buchungen — z.B. wenn ein Offline-Sync fehlgeschlagen ist.
            </CardDescription>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-4 w-4 text-gray-500" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm"
              data-testid="tc-filter-status"
            >
              <option value="">Alle Status</option>
              <option value="running">Laufend</option>
              <option value="closed">Abgeschlossen</option>
            </select>
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1.5 text-white text-sm"
              data-testid="tc-filter-days"
            >
              <option value={7}>7 Tage</option>
              <option value={14}>14 Tage</option>
              <option value={30}>30 Tage</option>
              <option value={90}>90 Tage</option>
            </select>
            <Button size="sm" variant="outline" onClick={load} className="border-gray-700" data-testid="tc-reload">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading && entries.length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm">Lade Einträge…</p>
        ) : entries.length === 0 ? (
          <p className="text-center py-8 text-gray-500 text-sm">Keine Zeit-Buchungen im Zeitraum.</p>
        ) : (
          <div className="space-y-2">
            {entries.map((e) => {
              const patch = editing[e._id] || {};
              const isCorrected = e.manually_corrected;
              const isOffline = e.start_source === 'offline_sync' || e.stop_source === 'offline_sync';
              return (
                <div key={e._id} className="p-3 rounded-lg bg-black/40 border border-gray-800 space-y-2" data-testid={`tc-row-${e._id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <p className="text-white font-medium text-sm">
                        {e.employee_name} <span className="text-gray-500">·</span> <span className="text-gray-400">{e.project_name}</span>
                      </p>
                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                        <Badge className={e.status === 'running' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30' : 'bg-gray-700/40 text-gray-300 border border-gray-600'}>
                          {e.status === 'running' ? 'läuft' : 'abgeschlossen'}
                        </Badge>
                        <Badge className="bg-sky-500/15 text-sky-300 border border-sky-500/30 text-xs">
                          {formatDuration(e.duration_minutes)}
                        </Badge>
                        {isOffline && (
                          <Badge className="bg-amber-500/15 text-amber-300 border border-amber-500/40 text-xs" title="Offline synchronisiert">
                            Offline-Sync
                          </Badge>
                        )}
                        {isCorrected && (
                          <Badge className="bg-purple-500/15 text-purple-300 border border-purple-500/40 text-xs">
                            Korrigiert
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="ghost" onClick={() => remove(e)} className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" data-testid={`tc-delete-${e._id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <div>
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wider">Start</Label>
                      <Input
                        type="datetime-local"
                        value={patch.started_at ?? toLocalDateTimeInput(e.started_at)}
                        onChange={(ev) => setPatch(e._id, 'started_at', ev.target.value)}
                        className="bg-gray-800 border-gray-700 text-white h-9 text-sm"
                        data-testid={`tc-start-${e._id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wider">Ende</Label>
                      <Input
                        type="datetime-local"
                        value={patch.ended_at ?? toLocalDateTimeInput(e.ended_at)}
                        onChange={(ev) => setPatch(e._id, 'ended_at', ev.target.value)}
                        className="bg-gray-800 border-gray-700 text-white h-9 text-sm"
                        data-testid={`tc-end-${e._id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] text-gray-500 uppercase tracking-wider">Notiz</Label>
                      <Input
                        value={patch.note ?? (e.note || '')}
                        onChange={(ev) => setPatch(e._id, 'note', ev.target.value)}
                        className="bg-gray-800 border-gray-700 text-white h-9 text-sm"
                        data-testid={`tc-note-${e._id}`}
                        placeholder="Notiz…"
                      />
                    </div>
                  </div>

                  {Object.keys(patch).length > 0 && (
                    <div className="flex items-center justify-between pt-1">
                      <div className="flex items-center gap-1.5 text-xs text-amber-300">
                        <AlertTriangle className="h-3.5 w-3.5" /> Nicht gespeicherte Änderungen
                      </div>
                      <Button
                        size="sm"
                        onClick={() => save(e)}
                        disabled={saving[e._id]}
                        className="bg-yellow-500 hover:bg-yellow-400 text-black h-8"
                        data-testid={`tc-save-${e._id}`}
                      >
                        <Save className="h-3.5 w-3.5 mr-1" /> {saving[e._id] ? 'Speichere…' : 'Speichern'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TimeCorrectionPanel;
